import type { Content, Route, RouteStep, RouteTrack, StepView } from "@/app/queries";
import type { StepState } from "@/ui/route";
import { count } from "@/lib/plural";

/* Чистые функции карты и трека (S2/S3): всё считается из данных маршрута
   и документа шага, ничего не пишется строкой. */

export function mainTracks(r: Route): RouteTrack[] {
  return r.tracks.filter((t) => !t.parallel);
}

export function parallelTracks(r: Route): RouteTrack[] {
  return r.tracks.filter((t) => t.parallel);
}

export type TrackTone = "done" | "active" | "rest";

/** Тон трека: все гейты сданы · есть шаг в работе · закрыт. */
export function trackTone(t: RouteTrack): TrackTone {
  if (t.total > 0 && t.done === t.total) return "done";
  if (t.steps.some((s) => s.state === "active")) return "active";
  return "rest";
}

export function findStep(r: Route, id: string): { track: RouteTrack; step: RouteStep } | undefined {
  for (const track of r.tracks) {
    const step = track.steps.find((s) => s.id === id);
    if (step) return { track, step };
  }
  return undefined;
}

/** Шаг, выбранный по умолчанию: активный, иначе первый шаг цепочки. */
export function defaultStepId(r: Route): string | undefined {
  return r.active?.id ?? r.tracks.find((t) => !t.parallel)?.steps[0]?.id ?? r.tracks[0]?.steps[0]?.id;
}

/** Что откроет гейт: следующий шаг трека, у последнего — следующий трек.
 *  У параллельного трека — шаг min(n+1, total), последний открывает себя. */
export function nextAfter(track: RouteTrack, step: RouteStep): string {
  if (track.parallel) {
    const pos = Math.min(step.position + 1, track.total);
    return track.steps.find((s) => s.position === pos)?.title ?? step.title;
  }
  return track.steps.find((s) => s.position === step.position + 1)?.title ?? "Следующий трек маршрута";
}

/** Причина блокировки — рядом с заблокированной кнопкой всегда есть почему. */
export function lockReason(step: RouteStep): string {
  return step.position > 1 ? `откроется после гейта шага ${step.position - 1}` : "откроется после гейта предыдущего трека";
}

export const STEP_BUTTON: Record<StepState, string> = {
  done: "открыть шаг",
  active: "продолжить шаг",
  open: "начать шаг",
  locked: "сдай предыдущий гейт",
};

/* ── ресурсы и практика из документа шага ───────────────────── */

type Ticks = StepView["ticks"];
type Resource = Content["resources"][number];
type Task = Content["practice"]["base"][number];

export type ResourceProgress = {
  /** Отмеченных юнитов; у ресурса без гайда — 1, если отмечен целиком. */
  done: number;
  /** Юнитов гайда; 0 — «без гайда». */
  total: number;
  full: boolean;
  started: boolean;
};

export function resourceProgress(r: Resource, ticks: Ticks): ResourceProgress {
  if (r.units.length === 0) {
    const full = ticks.resources.includes(r.id);
    return { done: full ? 1 : 0, total: 0, full, started: full };
  }
  // ⏭️-юниты в гейт не входят и в прогресс ресурса не считаются.
  const units = r.units.filter((u) => !u.skip);
  const done = units.filter((u) => ticks.units.includes(u.key)).length;
  return { done, total: units.length, full: done === units.length, started: done > 0 };
}

/** Ресурсы гейта: «Первоисточник» (optional) — вне гейта. */
export function requiredResources(c: Content): Resource[] {
  return c.resources.filter((r) => !r.optional);
}

/** Ресурсов гейта, где отмечены все юниты (или ресурс без гайда отмечен целиком). */
export function resourcesDone(view: StepView): number {
  return requiredResources(view.content).filter((r) => resourceProgress(r, view.ticks).full).length;
}

export function tasksDone(tasks: Task[], ticks: Ticks): number {
  return tasks.filter((t) => ticks.tasks.includes(t.id)).length;
}

export function tasksTotal(c: Content): number {
  return c.practice.base.length + c.practice.apply.length;
}

/** Мета шага в аккордеоне: «3 ресурса · 8 задач практики · банк вопросов «EDA»». */
export function stepMeta(c: Content): string {
  const parts = [count(requiredResources(c).length, "ресурс", "ресурса", "ресурсов"), `${count(tasksTotal(c), "задача", "задачи", "задач")} практики`];
  if (c.gate.bank.name) parts.push(`банк вопросов «${c.gate.bank.name}»`);
  return parts.join(" · ");
}

/** Подводка трека: для ML — текст макета S3, остальные — из числа шагов. */
export function trackLead(t: RouteTrack): string {
  if (t.slug === "ml") {
    return "Шесть шагов: от разведочного анализа до финального проекта. Шаг открывается, когда сдан гейт предыдущего. Параллельно идёт трек «Алгоритмы».";
  }
  const n = count(t.total, "шаг", "шага", "шагов");
  return t.parallel ? `${n}. Шаги открыты всегда и не блокируют основную цепочку.` : `${n}. Шаг открывается, когда сдан гейт предыдущего.`;
}

/** Описание шага в панели S2 — тексты макета для известных шагов, иначе общий состав. */
export function stepDescription(track: RouteTrack, step: RouteStep): string {
  if (track.parallel) return "Параллельный трек. Не блокирует основную цепочку, но гейт закрывается так же — через задачи и банк вопросов.";
  if (track.slug === "ml" && step.position === 1) {
    return "Три ресурса, «Проверь себя» на 4 вопроса, типичные ошибки, пример кода, заметки менторов и практика двумя уровнями по 4 задачи.";
  }
  if (track.slug === "py" && step.position === 2) return "NumPy и Pandas. Домашка hw2_library_data.ipynb — 14 задач сквозного сюжета «Библиотека».";
  return "Состав шага: ресурсы с гайдами по юнитам, «Проверь себя», типичные ошибки, пример кода, заметки менторов, практика двух уровней и критерии гейта «Готов к переходу».";
}
